/**
 * Card: the one container for console sections (D-2605). Header carries a title (h2 by default), optional
 * description and actions; the body stacks its children; `flush` removes body padding for tables.
 */
import type { ElementType, ReactNode } from 'react';

export type CardTone = 'neutral' | 'accent' | 'success' | 'warning' | 'error';

export interface CardProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Actions aligned to the right of the title. */
  actions?: ReactNode;
  icon?: ReactNode;
  /** Heading level of the title (default h2). */
  as?: 'h2' | 'h3';
  /** Body without padding (tables, lists that draw their own rows). */
  flush?: boolean;
  tone?: CardTone;
  footer?: ReactNode;
  children?: ReactNode;
  className?: string;
  id?: string;
  /** Explicit `aria-labelledby` target id for the title. */
  titleId?: string;
  'data-testid'?: string;
  style?: React.CSSProperties;
}

export function Card({
  title,
  description,
  actions,
  icon,
  as = 'h2',
  flush = false,
  tone = 'neutral',
  footer,
  children,
  className,
  id,
  titleId,
  style,
  ...rest
}: CardProps) {
  const Heading: ElementType = as;
  const hasHeader = Boolean(title || actions || description);
  return (
    <section
      id={id}
      className={['wdc-card', className].filter(Boolean).join(' ')}
      data-tone={tone === 'neutral' ? undefined : tone}
      data-flush={flush ? 'true' : undefined}
      aria-labelledby={titleId}
      style={style}
      data-testid={rest['data-testid']}
    >
      {hasHeader ? (
        <div className="wdc-card__header">
          <div className="wdc-card__heading">
            {title ? (
              <Heading className="wdc-card__title" id={titleId}>
                {icon}
                {title}
              </Heading>
            ) : null}
            {description ? <p className="wdc-card__desc">{description}</p> : null}
          </div>
          {actions ? <div className="wdc-card__actions">{actions}</div> : null}
        </div>
      ) : null}
      {children !== undefined && children !== null ? <div className="wdc-card__body">{children}</div> : null}
      {footer ? <div className="wdc-card__footer">{footer}</div> : null}
    </section>
  );
}

/** Horizontal rule-style section title used outside cards. */
export function SectionTitle({
  title,
  description,
  actions,
  as = 'h2',
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  as?: 'h2' | 'h3';
}) {
  const Heading: ElementType = as;
  return (
    <div className="wdc-section-title">
      <div>
        <Heading>{title}</Heading>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="wd-btn-group">{actions}</div> : null}
    </div>
  );
}
